import React, { useEffect, useRef } from "react";
import "./style.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Crown, TrendingUp, Users, Gift } from "lucide-react";
import p1 from "../../Assests/p-1.jpg";
import p2 from "../../Assests/p-2.jpg";
import p3 from "../../Assests/p-3.jpg";
import p4 from "../../Assests/p-4.jpg";

gsap.registerPlugin(ScrollTrigger);

const WHY_POINTS = [
  {
    id: "leader",
    index: "01",
    Icon: Crown,
    title: "Be A Leader",
    description:
      "Represent your college as you help organize one of India's largest sports fests.",
  },
  {
    id: "skills",
    index: "02",
    Icon: TrendingUp,
    title: "Enhance Your Skills",
    description:
      "Sharpen your communication and managerial skills through real, on-ground responsibility.",
  },
  {
    id: "network",
    index: "03",
    Icon: Users,
    title: "Networking",
    description:
      "Interact with people from diverse fields across the country to develop your network.",
  },
  {
    id: "courses",
    index: "04",
    Icon: Gift,
    title: "Free Courses",
    description:
      "Top-performing CAs get access to online courses and a performance-based earning opportunity.",
  },
];

const whyGridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

const whyCardVariants = {
  hidden: { opacity: 0, y: 36 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const RESPONSIBILITIES = [
  {
    id: "advertise",
    index: "01",
    title: "Advertise",
    description:
      "Publicize Udghosh and its sponsors in your college by sharing posts and promoting content by becoming a focal point for your respective college.",
    image: p1,
    accentA: "#e7c07a",
    accentB: "#b8873c",
  },
  {
    id: "organize",
    index: "02",
    title: "Organize",
    description:
      "Organize events, workshops and sessions regarding Udghosh and what it has to offer with assistance from mentors.",
    image: p2,
    accentA: "#e9b98a",
    accentB: "#9c6b2e",
  },
  {
    id: "conduct",
    index: "03",
    title: "Conduct",
    description:
      "Help in management of elimination rounds in your college and city to select participants for the main event.",
    image: p3,
    accentA: "#f0cf94",
    accentB: "#a67a35",
  },
  {
    id: "coordinate",
    index: "04",
    title: "Coordinate",
    description:
      "Collaborate with the Udghosh team in organising on-ground events in your city.",
    image: p4,
    accentA: "#ecc98b",
    accentB: "#8f6428",
  },
];

export default function Featured() {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const sceneRefs = useRef([]);
  const dotRefs = useRef([]);
  const bgRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Modern browsers scroll on a separate thread from JS. Inside a
      // pinned + scrubbed section, that can let the perceived scroll
      // position drift out of sync with a trackpad's continuous stream of
      // small delta events — and on some setups it stalls out completely
      // (mouse-wheel "notches" are coarse enough to not show this; arrow
      // keys bypass it entirely since they trigger native scroll-by-line
      // rather than a wheel/trackpad event stream). normalizeScroll moves
      // scroll handling onto the JS thread so it can't desync. This is
      // GSAP's own documented fix for exactly this symptom.
      ScrollTrigger.normalizeScroll(true);

      const scenes = sceneRefs.current.filter(Boolean);
      const dots = dotRefs.current.filter(Boolean);
      const count = scenes.length;

      // Initial state
      gsap.set(scenes, { autoAlpha: 0 });
      gsap.set(scenes[0], { autoAlpha: 1 });
      scenes.forEach((scene, i) => {
        const visual = scene.querySelector(".scene-visual");
        const heading = scene.querySelector(".scene-heading");
        const desc = scene.querySelector(".scene-desc");
        const eyebrow = scene.querySelector(".scene-eyebrow");
        gsap.set(visual, {
          xPercent: i === 0 ? 0 : 12,
          yPercent: i === 0 ? 0 : -6,
          scale: i === 0 ? 1 : 0.88,
          rotate: i === 0 ? 0 : 6,
        });
        gsap.set([eyebrow, heading, desc], {
          opacity: i === 0 ? 1 : 0,
          y: i === 0 ? 0 : 26,
        });
      });
      dots.forEach((d, i) => d.classList.toggle("is-active", i === 0));

      const segment = 1; // 1 "unit" per transition

      const tl = gsap.timeline({
        defaults: { ease: "power2.inOut" },
        scrollTrigger: {
          trigger: rootRef.current,
          start: "top top",
          end: () => "+=" + Math.round(window.innerHeight * 4.5),
          pin: true,
          scrub: 0.6,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          // NOTE: no `snap` here on purpose — GSAP snapping fights the
          // continuous momentum stream a trackpad (or a touch-drag, which
          // is what DevTools' mobile emulation sends) produces: snap keeps
          // trying to pull the scroll position back to the nearest scene
          // boundary while the trackpad is still feeding it delta events,
          // and the two fight each other, which reads as "scroll is stuck".
          // Mouse-wheel notches are coarse enough that this is barely
          // noticeable, which is why it looked wheel-only.
        },
      });

      for (let i = 0; i < count - 1; i++) {
        const outgoing = scenes[i];
        const incoming = scenes[i + 1];
        const outVisual = outgoing.querySelector(".scene-visual");
        const inVisual = incoming.querySelector(".scene-visual");
        const outText = [
          outgoing.querySelector(".scene-eyebrow"),
          outgoing.querySelector(".scene-heading"),
          outgoing.querySelector(".scene-desc"),
        ];
        const inText = [
          incoming.querySelector(".scene-eyebrow"),
          incoming.querySelector(".scene-heading"),
          incoming.querySelector(".scene-desc"),
        ];

        const pos = i * segment;

        // Outgoing scene exits — diagonal, scale down
        // (transform + opacity only — no filter/blur tweens, since those
        // aren't GPU-compositable and can drop frames badly during scrub)
        tl.to(
          outVisual,
          {
            xPercent: -14,
            yPercent: 8,
            scale: 0.86,
            rotate: -7,
          },
          pos
        )
          .to(outText, { opacity: 0, y: -22, stagger: 0.03 }, pos)
          .to(outgoing, { autoAlpha: 0, duration: segment * 0.4 }, pos + segment * 0.55)

          // Incoming scene enters — diagonal from opposite corner
          .fromTo(
            incoming,
            { autoAlpha: 0 },
            { autoAlpha: 1, duration: segment * 0.3 },
            pos + segment * 0.15
          )
          .fromTo(
            inVisual,
            { xPercent: 14, yPercent: -8, scale: 0.86, rotate: 7 },
            { xPercent: 0, yPercent: 0, scale: 1, rotate: 0 },
            pos + segment * 0.15
          )
          .fromTo(
            inText,
            { opacity: 0, y: 26 },
            { opacity: 1, y: 0, stagger: 0.06 },
            pos + segment * 0.35
          )

          // Background gradient morph (color-only — the bg layer holds the
          // heavy blur(90px) glow blobs, so it deliberately isn't
          // transform-animated here: moving a blurred layer every frame
          // forces re-blurring instead of a cheap compositor move)
          .to(
            bgRef.current,
            {
              "--accentA": RESPONSIBILITIES[i + 1].accentA,
              "--accentB": RESPONSIBILITIES[i + 1].accentB,
            },
            pos
          )

          // Dots
          .add(() => {
            dots.forEach((d, di) => d.classList.toggle("is-active", di === i + 1));
          }, pos + segment * 0.5);
      }

      ScrollTrigger.refresh();
    }, rootRef);

    return () => {
      ctx.revert();
      ScrollTrigger.normalizeScroll(false);
    };
  }, []);

  return (
    <section className="featured-container" id="featured" data-scroll-container>
      <section className="why-ca-section section-fade" id="whyCA">
        <div className="why-ca-glow why-ca-glow-a" />
        <div className="why-ca-glow why-ca-glow-b" />
        <div className="why-ca-particles">
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="why-ca-particle"
              style={{
                left: `${(i * 43) % 100}%`,
                top: `${(i * 61) % 100}%`,
                animationDelay: `${(i % 6) * 0.7}s`,
                animationDuration: `${10 + (i % 5) * 1.4}s`,
              }}
            />
          ))}
        </div>

        <div className="udg-section-header why-ca-header">
          <motion.span
            className="udg-eyebrow"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            The Opportunity
          </motion.span>
          <motion.h2
            className="udg-section-title font-display"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            Why Become A Campus Ambassador?
          </motion.h2>
          <motion.span
            className="udg-gold-rule why-ca-rule"
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          />
          <motion.p
            className="udg-lede why-ca-lede"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Four reasons this role is worth far more than a line on your resume.
          </motion.p>
        </div>

        <motion.div
          className="why-ca-grid"
          variants={whyGridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {WHY_POINTS.map((point) => (
            <motion.div
              key={point.id}
              className="why-ca-card udg-card"
              variants={whyCardVariants}
              whileHover={{ y: -8 }}
            >
              <span className="why-ca-card-index">{point.index}</span>
              <span className="why-ca-icon">
                <point.Icon size={22} strokeWidth={1.4} />
              </span>
              <h3 className="why-ca-card-title">{point.title}</h3>
              <p className="why-ca-card-desc">{point.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <div className="resp-pin" ref={rootRef}>
        <div
          className="resp-stage-bg"
          ref={bgRef}
          style={{
            "--accentA": RESPONSIBILITIES[0].accentA,
            "--accentB": RESPONSIBILITIES[0].accentB,
          }}
        >
          <div className="resp-glow resp-glow-a" />
          <div className="resp-glow resp-glow-b" />
          <div className="resp-particles">
            {Array.from({ length: 16 }).map((_, i) => (
              <span
                key={i}
                className="resp-particle"
                style={{
                  "--i": i,
                  left: `${(i * 37) % 100}%`,
                  top: `${(i * 53) % 100}%`,
                  animationDelay: `${(i % 8) * 0.6}s`,
                  animationDuration: `${9 + (i % 5) * 1.6}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="resp-stage" ref={stageRef}>
          <p className="resp-kicker">Responsibilities</p>

          {RESPONSIBILITIES.map((item, i) => (
            <div
              className="scene"
              id={item.id}
              key={item.id}
              ref={(el) => (sceneRefs.current[i] = el)}
            >
              <div className="scene-visual">
                <div className="scene-visual-frame">
                  <img src={item.image} alt={item.title} />
                </div>
              </div>
              <div className="scene-content glass-panel">
                <span className="scene-eyebrow">{item.index} / 04</span>
                <h3 className="scene-heading">{item.title}</h3>
                <p className="scene-desc">{item.description}</p>
              </div>
            </div>
          ))}

          <div className="resp-dots">
            {RESPONSIBILITIES.map((item, i) => (
              <span
                key={item.id}
                className="resp-dot"
                ref={(el) => (dotRefs.current[i] = el)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
